// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Feature, Polygon as GeoJSONPolygon } from 'geojson';
import 'leaflet/dist/leaflet.css';
import './PolygonSelector.css';

interface Polygon {
  id: number;
  geometry: any; // GeoJSON geometry
  area_m2: number;
  bounds: [number, number, number, number]; // [minx, miny, maxx, maxy]
}

interface GeotiffUrls {
  rgb: string;
  mask: string;
  dsm: string;
  flux: string;
}

interface ProcessGeotiffResponse {
  success: boolean;
  geotiff_urls: GeotiffUrls;
  polygons: Polygon[];
  center: { lat: number; lng: number };
  bounds: { ne: any; sw: any };
  imagery_date: { year: number; month: number; day: number };
  imagery_quality: string;
}

interface PolygonSelectorProps {
  lat: number;
  lng: number;
  apiUrl?: string;
}

// Component to fit map bounds
function FitBounds({ bounds }: { bounds: any }): null {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [map, bounds]);

  return null;
}

const PolygonSelector: React.FC<PolygonSelectorProps> = ({
  lat,
  lng,
  apiUrl = 'http://localhost:5001',
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ProcessGeotiffResponse | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<Polygon | null>(null);
  const [mapBounds, setMapBounds] = useState<any>(null);

  useEffect(() => {
    fetchGeotiffData();
  }, [lat, lng]);

  const fetchGeotiffData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/api/solar/process-geotiff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lat, lng }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: ProcessGeotiffResponse = await response.json();

      if (!result.success) {
        throw new Error('Failed to process GeoTIFF');
      }

      setData(result);

      // Calculate map bounds from polygons
      if (result.polygons.length > 0) {
        const allCoords: [number, number][] = [];
        result.polygons.forEach((poly) => {
          if (poly.geometry.type === 'Polygon') {
            poly.geometry.coordinates[0].forEach((coord: number[]) => {
              allCoords.push([coord[1], coord[0]]); // [lat, lng]
            });
          }
        });

        if (allCoords.length > 0) {
          const bounds = L.latLngBounds(allCoords);
          setMapBounds(bounds);
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching GeoTIFF data:', err);
      setError(err.message || 'Failed to load GeoTIFF data');
      setLoading(false);
    }
  };

  const handlePolygonClick = (polygon: Polygon) => {
    setSelectedPolygon(polygon);
  };

  const getPolygonStyle = (polygon: Polygon): any => {
    const isSelected = selectedPolygon?.id === polygon.id;
    return {
      color: isSelected ? '#2563eb' : '#ef4444',
      fillColor: isSelected ? '#3b82f6' : '#f87171',
      fillOpacity: isSelected ? 0.5 : 0.3,
      weight: isSelected ? 3 : 2,
    };
  };

  if (loading) {
    return (
      <div className="polygon-selector-loading">
        <div className="spinner"></div>
        <p>Loading GeoTIFF data and extracting polygons...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="polygon-selector-error">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={fetchGeotiffData}>Retry</button>
      </div>
    );
  }

  if (!data) {
    return <div>No data available</div>;
  }

  return (
    <div className="polygon-selector-container">
      {/* Left Panel - Polygon List & Controls */}
      <div className="polygon-selector-sidebar">
        {/* Header with NIKO yellow border */}
        <div className="info-panel-header">
          <h2>Roof Polygon Selector</h2>
          <p className="subtitle">
            {data.imagery_date.year}-{data.imagery_date.month}-{data.imagery_date.day} |
            {data.imagery_quality} Quality |
            {data.polygons.length} Polygons
          </p>
        </div>

        {/* Polygon List */}
        <div className="info-panel">
          <h3>Available Polygons</h3>
          <div className="polygon-list">
            {data.polygons.map((polygon) => (
              <div
                key={polygon.id}
                className={`polygon-item ${
                  selectedPolygon?.id === polygon.id ? 'selected' : ''
                }`}
                onClick={() => handlePolygonClick(polygon)}
              >
                <div className="polygon-item-header">
                  <span className="polygon-id">#{polygon.id}</span>
                  <span className="polygon-area">
                    {polygon.area_m2.toFixed(2)} m²
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Selected Polygon Details */}
          {selectedPolygon && (
            <div className="selected-polygon-details">
              <h3>Selected Polygon</h3>
              <div className="details-content">
                <p><strong>ID:</strong> {selectedPolygon.id}</p>
                <p><strong>Area:</strong> {selectedPolygon.area_m2.toFixed(2)} m²</p>
                <div className="action-buttons">
                  <button className="btn-primary" disabled>
                    Edit Shape (Phase 3)
                  </button>
                  <button className="btn-secondary" disabled>
                    Draw Obstruction (Phase 4)
                  </button>
                  <button className="btn-success" disabled>
                    Save Polygon (Phase 5)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Map */}
      <div className="polygon-selector-map">
        <MapContainer
          center={[lat, lng] as any}
          zoom={18}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          {/* Satellite basemap */}
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Esri, Maxar, Earthstar Geographics"
            maxZoom={22}
          />

          {/* TODO: Add RGB GeoTIFF overlay here (Phase 1.5) */}
          {/* For now, using satellite basemap */}

          {/* Render polygons */}
          {data.polygons.map((polygon) => {
            const feature: Feature<GeoJSONPolygon> = {
              type: 'Feature',
              geometry: polygon.geometry,
              properties: { id: polygon.id },
            };
            return (
              <GeoJSON
                key={polygon.id}
                data={feature}
                style={getPolygonStyle(polygon)}
                eventHandlers={{
                  click: () => handlePolygonClick(polygon),
                }}
              />
            );
          })}

          {/* Fit map to polygon bounds */}
          {mapBounds && <FitBounds bounds={mapBounds} />}
        </MapContainer>
      </div>
    </div>
  );
};

export default PolygonSelector;
